import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Birds.css";

interface BirdItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Birds: React.FC = () => {
  const [data, setData] = useState<BirdItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/birds`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching birds:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: BirdItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: BirdItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Birds",
    de: "Vögel",
    ta: "பறவைகள்",
    si: "ගිණිසලකුරු",
    hi: "पक्षी",
    ar: "الطيور",
    tr: "Kuşlar",
    ru: "Птицы",
    zh: "鸟类",
    fr: "Oiseaux",
    es: "Pájaros",
  };

  const subtitleMap = {
    en: "Birds in German",
    de: "Vögel auf Deutsch",
    ta: "ஜெர்மனில் பறவைகள்",
    si: "ජර්මන් භාෂාවෙන් ගිණිසලකුරු",
    hi: "जर्मन में पक्षी",
    ar: "الطيور بالألمانية",
    tr: "Almanca'da Kuşlar",
    ru: "Птицы на немецком",
    zh: "德语鸟类",
    fr: "Oiseaux en allemand",
    es: "Pájaros en alemán",
  };

  return (
    <div className="birds-page-container">
      <div className="birds-page-header">
        <div>
          <h1 className="birds-page-title">{titleMap["de"]}</h1>
          <p className="birds-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="birds-page-loading">Loading...</div>
      ) : (
        <div className="birds-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`birds-page-card ${
                  isPlaying ? "birds-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="birds-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="birds-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="birds-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="birds-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="birds-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Birds;
